import type { DaemonHealth, NormalizedRun, OperationsCostSummary } from '../../api/types'
import type { AABB } from '../colliders'

export const EXCHANGE_COST_STUDIO = {
  centerX: -9,
  centerZ: -4.6,
  width: 7.2,
  depth: 7,
  height: 2.65,
  frontZ: -1.1,
  doorWidth: 1.5,
  material: 'glass',
} as const

/** Clear threshold and approach for the cost studio's front glass door. */
export const EXCHANGE_COST_DOOR_APPROACH: AABB = {
  minX: -9.75,
  maxX: -8.25,
  minY: 0,
  maxY: EXCHANGE_COST_STUDIO.height,
  minZ: -1.25,
  maxZ: 0.8,
}

/** Physical envelope of the licensed central table and its four seats. */
export const EXCHANGE_LIVE_FLOW_TABLE: AABB = {
  minX: -2.75,
  maxX: 2.75,
  minY: 0,
  maxY: 0.82,
  minZ: -6.6,
  maxZ: -2.6,
}

/** Player-facing interaction edge and clear standing envelope south of the table. */
export const EXCHANGE_LIVE_FLOW_INTERACTION = {
  x: 0,
  z: -2.15,
  approach: {
    minX: -1.2,
    maxX: 1.2,
    minY: 0,
    maxY: 2.4,
    minZ: -2.6,
    maxZ: 0,
  } satisfies AABB,
} as const

/** Capacity console anchor, with a minimum 1.2 m clear operator envelope. */
export const EXCHANGE_CAPACITY_STATION = {
  x: 9.05,
  z: -5.4,
  approach: {
    minX: 6.4,
    maxX: 11.7,
    minY: 0,
    maxY: 2.4,
    minZ: -3.4,
    maxZ: -2.15,
  } satisfies AABB,
} as const

/** Shared standing review point in front of the two-seat capacity operator bank. */
export const EXCHANGE_CAPACITY_REVIEW_POINT = {
  x: 9.05,
  z: -3.25,
  approach: {
    minX: 8.45,
    maxX: 9.65,
    minY: 0,
    maxY: 2.4,
    minZ: -3.4,
    maxZ: -2.15,
  } satisfies AABB,
} as const

/** Unobstructed 1.2 m route from the front entry to the live-flow interaction edge. */
export const EXCHANGE_CENTRAL_SPINE: AABB = {
  minX: -0.6,
  maxX: 0.6,
  minY: 0,
  maxY: 2.4,
  minZ: 0,
  maxZ: 9,
}

export const MAX_EXCHANGE_RUNS = 8

export type ExchangeProjectionStatus = 'disconnected' | 'loading' | 'fresh' | 'stale'

export interface ExchangeProjectionState {
  state: 'fresh-populated' | 'fresh-empty' | 'loading' | 'stale-last-good' | 'disconnected'
  /** Bounded, deterministic subset available for physical run-slot rendering. */
  runs: readonly NormalizedRun[]
  runningTotal: number | null
  runOverflow: number | null
  queueDepth: number | null
  capacity: { active: number; total: number } | null
  costSummary: OperationsCostSummary | null
}

const unavailableProjection = (
  state: 'loading' | 'disconnected',
): ExchangeProjectionState => ({
  state,
  runs: [],
  runningTotal: null,
  runOverflow: null,
  queueDepth: null,
  capacity: null,
  costSummary: null,
})

const finiteCount = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : null

const validCostSummary = (
  summary: OperationsCostSummary | undefined,
): OperationsCostSummary | null => {
  if (!summary) return null
  const values = [
    summary.totalTokens,
    summary.totalCostUsd,
    summary.reportedUsd,
    summary.estimatedUsd,
  ]
  if (values.some((value) => !Number.isFinite(value) || value < 0)) return null
  return { ...summary }
}

const compareText = (first: string, second: string): number =>
  first < second ? -1 : first > second ? 1 : 0

const compareRuns = (first: NormalizedRun, second: NormalizedRun): number =>
  compareText(second.updatedAt, first.updatedAt) || compareText(first.id, second.id)

const cloneLastGood = (lastGood?: ExchangeProjectionState): ExchangeProjectionState => ({
  state: 'stale-last-good',
  runs: lastGood?.runs.map((run) => ({ ...run })) ?? [],
  runningTotal: lastGood?.runningTotal ?? null,
  runOverflow: lastGood?.runOverflow ?? null,
  queueDepth: lastGood?.queueDepth ?? null,
  capacity: lastGood?.capacity ? { ...lastGood.capacity } : null,
  costSummary: lastGood?.costSummary ? { ...lastGood.costSummary } : null,
})

/**
 * Pure projection of Portal-owned operations truth into the Exchange's bounded
 * physical surfaces. Unavailable values stay null rather than becoming zeros.
 */
export function layoutExchangeProjection(
  status: ExchangeProjectionStatus,
  health: DaemonHealth | null,
  runs: readonly NormalizedRun[],
  lastGood?: ExchangeProjectionState,
): ExchangeProjectionState {
  if (status === 'stale') return cloneLastGood(lastGood)
  if (status !== 'fresh') return unavailableProjection(status)

  const activeRuns = runs
    .filter((run) => run.status === 'running' && !run.stale)
    .slice()
    .sort(compareRuns)
  const queueDepth = health ? finiteCount(health.queueDepth) : null
  const active = health ? finiteCount(health.activeAgents) : null
  const total = health ? finiteCount(health.poolSize) : null
  const capacity = active !== null && total !== null && active <= total
    ? { active, total }
    : null
  const costSummary = validCostSummary(health?.costSummary)
  const populated = activeRuns.length > 0 ||
    (queueDepth !== null && queueDepth > 0) ||
    (capacity !== null && capacity.active > 0) ||
    (costSummary !== null && (costSummary.totalTokens > 0 || costSummary.totalCostUsd > 0))

  return {
    state: populated ? 'fresh-populated' : 'fresh-empty',
    runs: activeRuns.slice(0, MAX_EXCHANGE_RUNS),
    runningTotal: activeRuns.length,
    runOverflow: Math.max(0, activeRuns.length - MAX_EXCHANGE_RUNS),
    queueDepth,
    capacity,
    costSummary,
  }
}

const compactNumber = (value: number): string => new Intl.NumberFormat('en', {
  notation: value >= 10_000 ? 'compact' : 'standard',
  maximumFractionDigits: 1,
}).format(value)

const projectionPrefix = (state: ExchangeProjectionState): string =>
  state.state === 'stale-last-good' ? 'STALE · ' : ''

/** Physical panel copy derived only from a single Exchange projection snapshot. */
export function exchangeProjectionLines(
  state: ExchangeProjectionState,
): readonly [string, string, string] {
  if (state.state === 'loading') {
    return [
      'COST WINDOW · PROJECTING',
      'LIVE FLOW · PROJECTING',
      'AGENT CAPACITY · PROJECTING',
    ]
  }
  if (state.state === 'disconnected') {
    return [
      'COST WINDOW · NOT PROJECTED',
      'LIVE FLOW · NOT PROJECTED',
      'AGENT CAPACITY · NOT PROJECTED',
    ]
  }

  const prefix = projectionPrefix(state)
  const cost = state.costSummary
    ? `$${state.costSummary.totalCostUsd.toFixed(2)} · ${compactNumber(state.costSummary.totalTokens)} TOKENS`
    : 'COST WINDOW · NOT PROJECTED'
  const liveFlow = state.runningTotal === null
    ? 'LIVE FLOW · NOT PROJECTED'
    : state.queueDepth === null
      ? `${state.runningTotal} RUNNING · QUEUE NOT PROJECTED`
      : `${state.runningTotal} RUNNING · ${state.queueDepth} QUEUED`
  const capacity = state.capacity
    ? `${state.capacity.active} / ${state.capacity.total} AGENT SLOTS ACTIVE`
    : 'AGENT CAPACITY · NOT PROJECTED'

  return [`${prefix}${cost}`, `${prefix}${liveFlow}`, `${prefix}${capacity}`]
}

/** Stable semantic key for avoiding needless physical-surface repainting. */
export function exchangeProjectionKey(state: ExchangeProjectionState): string {
  return JSON.stringify({
    state: state.state,
    runs: state.runs.map((run) => [
      run.id,
      run.taskId,
      run.title,
      run.currentPhase ?? null,
      run.activeAgents,
      run.updatedAt,
    ]),
    runningTotal: state.runningTotal,
    runOverflow: state.runOverflow,
    queueDepth: state.queueDepth,
    capacity: state.capacity,
    costSummary: state.costSummary,
  })
}
